import {
  IHealthCheckRuleConfig,
  IHealthCheckRuleConfigType,
  IHealthCheckRuleOverride,
} from '../../Interfaces/IHealthCheckRule';
import { HEALTH_CHECK_CATALOG } from './catalog';
import {
  HealthCheckParams,
  HealthCheckSeverity,
  IHealthCheckRuleDefinition,
  IHealthCheckRuleState,
} from './types';

interface IResolveInput {
  /** Globale Konfiguration; fehlende Regeln fallen auf den Katalog zurück. */
  configArray: IHealthCheckRuleConfig[];
  /** Verknüpfungen Regel <-> Rechtsgeschäftsart. */
  configTypeArray: IHealthCheckRuleConfigType[];
  /** Abweichungen dieses Vorgangs. */
  overrideArray: IHealthCheckRuleOverride[];
  /** Rechtsgeschäftsart des Vorgangs, gegen die der Geltungsbereich prüft. */
  idLegalTransactionType: number;
  /** Namen der Rechtsgeschäftsarten für die Herkunftstexte. */
  legalTransactionTypeName?: string;
}

const SEVERITIES: HealthCheckSeverity[] = ['info', 'warning', 'error'];

const parseSeverity = (
  value: string | null,
  fallback: HealthCheckSeverity
): HealthCheckSeverity =>
  SEVERITIES.includes(value as HealthCheckSeverity)
    ? (value as HealthCheckSeverity)
    : fallback;

/**
 * Parameter der Regel: Katalog-Default, überschrieben von dem, was in der
 * Konfiguration steht. Ungültiges JSON wird ignoriert statt zu werfen — eine
 * kaputte Konfigurationszeile darf den Health Check nicht lahmlegen.
 */
const parseParams = (
  definition: IHealthCheckRuleDefinition,
  raw: string | null
): HealthCheckParams => {
  const params: HealthCheckParams = {};

  (definition.params ?? []).forEach(param => {
    params[param.key] = param.default;
  });

  if (!raw) {
    return params;
  }

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;

    (definition.params ?? []).forEach(param => {
      const value = parsed[param.key];

      if (param.type === 'number' && typeof value === 'number') {
        params[param.key] = value;
      }

      if (param.type === 'boolean' && typeof value === 'boolean') {
        params[param.key] = value;
      }
    });
  } catch {
    // Vorsätzlich still: Katalog-Defaults sind das sichere Verhalten.
  }

  return params;
};

/** Anzeigename für die Herkunft einer Vorgangs-Abweichung. */
const overrideAuthor = (override: IHealthCheckRuleOverride) => {
  const name = `${override.FirstName ?? ''} ${override.LastName ?? ''}`.trim();
  return name === '' ? null : name;
};

/**
 * Löst für genau einen Vorgang auf, welche Regeln greifen (#1283).
 *
 * Reihenfolge:
 *
 *  1. Gibt es eine Abweichung für diesen Vorgang, gewinnt sie. Ende.
 *  2. Sonst gilt die globale Zeile, oder — wenn keine existiert — der
 *     Katalog-Default.
 *  3. `IsActive` ist dabei der Hauptschalter, `ScopeMode` verengt nur innerhalb
 *     von aktiv. Der Geltungsbereich kann eine global abgeschaltete Regel also
 *     nicht wieder anschalten.
 *
 * Neben dem Zustand liefert die Funktion dessen Herkunft mit. Das ist nicht
 * Komfort: die frühere, sehr ähnliche Konstruktion bei den Bewertungsbögen
 * (Vorgang > Rechtsgeschäftsart > Default) wurde zurückgebaut, weil niemand
 * mehr nachvollziehen konnte, warum etwas griff.
 */
export const resolveActiveRules = ({
  configArray,
  configTypeArray,
  overrideArray,
  idLegalTransactionType,
  legalTransactionTypeName,
}: IResolveInput): IHealthCheckRuleState[] => {
  const configByRuleKey = new Map(
    configArray.map(config => [config.RuleKey, config])
  );
  const overrideByRuleKey = new Map(
    overrideArray.map(override => [override.RuleKey, override])
  );

  const typeIdsByConfigId = new Map<number, Set<number>>();
  configTypeArray.forEach(entry => {
    const existing = typeIdsByConfigId.get(entry.idHealthCheckRuleConfig);

    if (existing) {
      existing.add(entry.idLegalTransactionType);
    } else {
      typeIdsByConfigId.set(
        entry.idHealthCheckRuleConfig,
        new Set([entry.idLegalTransactionType])
      );
    }
  });

  const typeLabel = legalTransactionTypeName
    ? `Rechtsgeschäftsart „${legalTransactionTypeName}"`
    : 'diese Rechtsgeschäftsart';

  return Object.entries(HEALTH_CHECK_CATALOG).map(([ruleKey, definition]) => {
    const config = configByRuleKey.get(ruleKey);
    const severity = parseSeverity(
      config?.Severity ?? null,
      definition.defaultSeverity
    );
    const params = parseParams(definition, config?.Params ?? null);

    const base: Omit<
      IHealthCheckRuleState,
      'isActive' | 'origin' | 'originLabel'
    > = { ruleKey, definition, severity, params };

    // 1. Abweichung für diesen Vorgang gewinnt immer.
    const override = overrideByRuleKey.get(ruleKey);
    if (override !== undefined) {
      const author = overrideAuthor(override);

      return {
        ...base,
        isActive: override.IsActive,
        origin: 'transaction',
        originLabel: author
          ? `für diesen Vorgang von ${author} ${
              override.IsActive ? 'aktiviert' : 'deaktiviert'
            }`
          : `für diesen Vorgang ${
              override.IsActive ? 'aktiviert' : 'deaktiviert'
            }`,
      };
    }

    // 2. Keine globale Zeile -> Katalog-Default.
    if (config === undefined) {
      return {
        ...base,
        isActive: definition.defaultActive,
        origin: 'catalog',
        originLabel: 'Standardvorgabe',
      };
    }

    // 3. Hauptschalter.
    if (!config.IsActive) {
      return {
        ...base,
        isActive: false,
        origin: 'global',
        originLabel: 'global deaktiviert',
      };
    }

    // 4. Geltungsbereich verengt innerhalb von aktiv.
    const scopedTypeIds =
      typeIdsByConfigId.get(config.idHealthCheckRuleConfig) ??
      new Set<number>();
    const isListed = scopedTypeIds.has(idLegalTransactionType);

    if (config.ScopeMode === 'include' && !isListed) {
      return {
        ...base,
        isActive: false,
        origin: 'scope',
        originLabel: `gilt nicht für ${typeLabel}`,
      };
    }

    if (config.ScopeMode === 'exclude' && isListed) {
      return {
        ...base,
        isActive: false,
        origin: 'scope',
        originLabel: `für ${typeLabel} ausgenommen`,
      };
    }

    return {
      ...base,
      isActive: true,
      origin: 'global',
      originLabel:
        config.ScopeMode === 'all'
          ? 'Vorgabe aus den Einstellungen'
          : `Vorgabe aus den Einstellungen (${typeLabel})`,
    };
  });
};
